/** @format */

/**
 * Event-Driven Version Checker
 * Checks for new versions only when user is actively using the app
 * No periodic polling - triggered by user events only
 * 
 * Performance benefits:
 * - Zero background activity when tab is hidden
 * - Only checks on page load, focus, and visibility changes
 * - No setInterval polling
 * - Minimal network usage
 */

let currentVersion = null;
let isUpdateAvailable = false;

/**
 * Initialize event-driven version checker
 * Only checks when user is actively using the app (no background polling)
 */
export const initVersionChecker = () => {
  // Get initial version on page load
  fetchCurrentVersion();

  // Check when page becomes visible (user switches back to tab)
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Check on page focus (user clicks into window)
  window.addEventListener('focus', handleWindowFocus);

  // Check on service worker update (if using service workers)
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', handleServiceWorkerUpdate);
  }

  console.log('✅ Event-driven version checker initialized (no background polling)');
};

/**
 * Fetch current version on initial load
 */
const fetchCurrentVersion = async () => {
  try {
    const response = await fetch('/version.json?' + Date.now(), {
      cache: 'no-cache',
      headers: { 'Cache-Control': 'no-cache' }
    });
    const data = await response.json();
    currentVersion = data.version;
    console.log('🔄 Current app version:', currentVersion);
  } catch (error) {
    console.warn('⚠️ Could not load version info:', error);
  }
};

/**
 * Handle visibility change (tab becomes visible)
 */
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible' && !isUpdateAvailable) {
    console.log('👁️ Tab visible - checking for updates');
    checkForNewVersion();
  }
};

/**
 * Handle window focus (user clicks into window)
 */
const handleWindowFocus = () => {
  if (!isUpdateAvailable) {
    console.log('🎯 Window focused - checking for updates');
    checkForNewVersion();
  }
};

/**
 * Handle service worker update
 */
const handleServiceWorkerUpdate = () => {
  console.log('🔄 Service Worker updated - new version available');
  isUpdateAvailable = true;
  showUpdateNotification();
};

/**
 * Check if a new version is available
 */
export const checkForNewVersion = async () => {
  // Skip if update already detected
  if (isUpdateAvailable || !currentVersion) {
    return isUpdateAvailable;
  }

  try {
    // Add timestamp to bypass cache
    const response = await fetch('/version.json?' + Date.now(), {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    const data = await response.json();
    
    if (data.version !== currentVersion) {
      console.log('🆕 New version detected:', data.version, '(current:', currentVersion + ')');
      isUpdateAvailable = true;
      showUpdateNotification(data);
      return true;
    }
    
    return false;
  } catch (error) {
    console.warn('❌ Version check failed:', error);
    return false;
  }
};

/**
 * Show notification to user about new version
 * Automatically refreshes the page immediately
 */
const showUpdateNotification = (newVersion) => {
  console.log('🔄 New version detected - Refreshing page immediately...');
  
  // Auto-refresh immediately
  forceRefresh();
};

/**
 * Force refresh the application
 * Clears cache and reloads
 */
export const forceRefresh = () => {
  // Clear service worker cache if present
  if ('serviceWorker' in navigator) {
    caches.keys().then(cacheNames => {
      cacheNames.forEach(cacheName => {
        caches.delete(cacheName);
      });
    });
    
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => {
        registration.unregister();
      });
    });
  }
  
  // Clear browser cache and reload
  window.location.reload(true);
};

/**
 * Get current app version
 */
export const getCurrentVersion = () => currentVersion;

/**
 * Stop version checking and cleanup
 */
export const stopVersionChecker = () => {
  // Remove event listeners
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('focus', handleWindowFocus);
  
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.removeEventListener('controllerchange', handleServiceWorkerUpdate);
  }
  
  console.log('🛑 Event-driven version checker stopped');
};

/**
 * Get version checker status
 */
export const getVersionCheckerStats = () => {
  return {
    currentVersion,
    isUpdateAvailable,
    checkingMethod: 'event-driven (no polling)'
  };
};
